import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import {
  fetchEventDetailsAction,
  toggleFavoriteEventAction,
} from "../../slices/eventDirectorySlice";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { ArrowLeft, Download, FileText, Star } from "lucide-react";
import { updateEventViewCount } from "@/modules/eventRegistration/features/EventDirectory/api/updateEventViewCount/updateEventViewCountApi";
import EventDetailsSkeleton from "@/modules/eventRegistration/features/EventDirectory/components/EventDetailsSkeleton/EventDetailsSkeleton";
import { downloadFile } from "@/api/downloadFile/downloadFileApi";
import EventDetailsSessionList from "@/modules/eventRegistration/features/EventDirectory/components/EventDetailsSessionList/EventDetailsSessionList";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { buildLegacyEventUrl } from "@/modules/eventRegistration/utils/buildLegacyEventUrl";
import "@/modules/hub/features/ResourceLibrary/components/ResourceDetailsContentBlock/ResourceDetailsContentBlock.css";

function EventDetails(): JSX.Element {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { eventDetails, fetchDetailsLoading, fetchDetailsError } =
    useAppSelector((state: RootState) => state.eventDirectory);

  useEffect(() => {
    if (eventDetails && !fetchDetailsLoading) {
      const timer = setTimeout(() => {
        if (eventDetails.uuid) {
          updateEventViewCount({ uuid: eventDetails.uuid }).catch((error) =>
            console.error("Error incrementing view count:", error),
          );
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [eventDetails, fetchDetailsLoading]);

  useEffect(() => {
    if (!uuid) return;
    dispatch(fetchEventDetailsAction(uuid));
  }, [dispatch, uuid]);

  useEffect(() => {
    if (eventDetails?.redirectToLegacy) {
      window.open(
        buildLegacyEventUrl("directory", eventDetails.legacyCourseId),
        "_blank",
        "noopener,noreferrer",
      );
      navigate("/event-registration/events", { replace: true });
    }
  }, [eventDetails, navigate]);

  const handleToggleFavorite = () => {
    if (eventDetails?.uuid) {
      dispatch(toggleFavoriteEventAction(eventDetails.uuid));
    }
  };

  const handleFileDownload = async (
    fileUuid: string,
    originalFileName?: string,
  ) => {
    try {
      const blob = await downloadFile({ fileUuid });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", originalFileName || "file");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("File download failed:", error);
    }
  };

  if (fetchDetailsLoading) {
    return (
      <MainPageWrapper hideHeader title="Event Directory">
        <EventDetailsSkeleton />
      </MainPageWrapper>
    );
  }

  if (fetchDetailsError || !eventDetails || !eventDetails.uuid) {
    return (
      <MainPageWrapper hideHeader title="Event Directory">
        <div className="py-16 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
            Event Not Found
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
            The event you're looking for doesn't exist or has been removed.
          </p>
          <button
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark dark:text-primary-light dark:hover:text-primary transition-colors"
            onClick={() => navigate("/event-registration/events")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Events
          </button>
        </div>
      </MainPageWrapper>
    );
  }

  const {
    eventName,
    eventDescription,
    eventPrice,
    eventTypeName,
    categories,
    thumbnailUrl,
    trades,
    roles,
    sessions,
    files,
    isFavorited,
    viewCount,
  } = eventDetails;

  const priceDisplay =
    eventPrice && eventPrice > 0 ? `$${eventPrice.toFixed(2)}` : "Free";

  const tags: string[] = [];
  if (eventTypeName) tags.push(eventTypeName);
  if (categories?.length) categories.forEach((c) => tags.push(c.name));
  if (trades?.length) trades.forEach((t) => tags.push(t.name));
  if (roles?.length) roles.forEach((r) => tags.push(r.name));

  return (
    <MainPageWrapper hideHeader title="Event Details">
      {/* Back navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          onClick={() => navigate("/event-registration/events")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Events
        </button>
        <button
          aria-label="Toggle Favorite"
          className="p-2 -mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          onClick={handleToggleFavorite}
        >
          <Star
            className={`h-5 w-5 ${
              isFavorited
                ? "text-yellow-500 fill-yellow-500"
                : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            } transition-colors`}
          />
        </button>
      </div>

      {/* Compact header: thumbnail + info */}
      <div className="flex gap-6 mb-6">
        {thumbnailUrl ? (
          <div className="flex-shrink-0 w-48 aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800">
            <img
              alt={eventName || "Event"}
              className="w-full h-full object-cover"
              src={thumbnailUrl}
            />
          </div>
        ) : (
          <div className="flex-shrink-0 w-48 aspect-video rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
            <span className="text-sm text-gray-400 dark:text-gray-500">
              No Image
            </span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
            {eventTypeName && (
              <span className="font-medium text-primary">{eventTypeName}</span>
            )}
            <span>{viewCount ?? 0} views</span>
            <span
              className={`font-semibold ${
                eventPrice && eventPrice > 0
                  ? "text-gray-900 dark:text-white"
                  : "text-green-600 dark:text-green-400"
              }`}
            >
              {priceDisplay}
            </span>
          </div>

          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white tracking-tight leading-tight mb-3">
            {eventName}
          </h1>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  className="text-xs px-2 py-0.5 rounded-full border border-gray-200 text-gray-600 dark:border-slate-700 dark:text-gray-400"
                  key={tag}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:gap-10">
        <div className="order-2 lg:order-1 lg:flex-1 lg:min-w-0">
          <DescriptionBlock eventDescription={eventDescription} />

          {files && files.length > 0 && (
            <div className={`${eventDescription ? "mt-8" : ""}`}>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">
                Attachments
              </h2>
              <div className="space-y-2">
                {files.map((file) => (
                  <button
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-all text-left group"
                    key={file.id}
                    onClick={() =>
                      handleFileDownload(file.uuid, file.originalFileName)
                    }
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                      {file.originalFileName || `File #${file.id}`}
                    </span>
                    <Download className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column: sessions (always visible, sticky on desktop) */}
        <div className="order-1 lg:order-2 lg:w-[400px] lg:shrink-0 mb-8 lg:mb-0">
          <div className="lg:sticky lg:top-4">
            {sessions && sessions.length > 0 ? (
              <EventDetailsSessionList sessions={sessions} />
            ) : (
              <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-6 text-center">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                  Sessions
                </h2>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No sessions available yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
}

function DescriptionBlock({ eventDescription }: { eventDescription: string }) {
  if (!eventDescription) return null;

  return (
    <div>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">
        About this event
      </h2>
      <div className="resource-markdown text-[15px] leading-relaxed text-gray-700 dark:text-gray-300">
        <ReactMarkdown
          components={{
            a: ({ children, href, ...props }) => (
              <a
                className="text-primary hover:text-primary-dark dark:text-primary-light transition-colors underline underline-offset-2"
                href={href}
                rel="noopener noreferrer"
                target="_blank"
                {...props}
              >
                {children}
              </a>
            ),
          }}
          remarkPlugins={[remarkGfm]}
        >
          {eventDescription}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default EventDetails;
